import React, { useState } from "react";
import { Eye, MessageSquare, Footprints, Scissors, Timer, ArrowRightLeft, Coffee, Star, ShieldCheck, TrendingUp, ChevronDown, X, Check } from "lucide-react";
import { SC_COLORS } from "@/data/superCerveau";
import { GUIDE_INTRO, PRINCIPLES, STOP_STRATEGY, FIVE_QUESTIONS, PHRASES, PRIORITIES } from "@/data/superCerveauGuide";
import { Panel } from "./scShared";
import { GuidePdfButton } from "./ScGuidePdf";

const ICONS = { Eye, MessageSquare, Footprints, Scissors, Timer, ArrowRightLeft, Coffee, Star, ShieldCheck, TrendingUp };

const PrincipleCard = ({ p, open, onToggle }) => {
  const c = SC_COLORS[p.color];
  const Icon = ICONS[p.icon];
  return (
    <div className="rounded-3xl border-2 bg-white/85 overflow-hidden transition-shadow hover:shadow-md" style={{ borderColor: open ? c.ring : "transparent" }} data-testid={`sc-guide-principle-${p.n}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-4 p-4 sm:p-5 text-left" data-testid={`sc-guide-toggle-${p.n}`}>
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-inner" style={{ background: c.bg, color: c.fg }}><Icon size={26} strokeWidth={2.2} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: c.fg }}>Principe {p.n}</div>
          <h3 className="font-serif-title text-xl sm:text-2xl text-[var(--ink)] leading-tight">{p.title}</h3>
        </div>
        <ChevronDown size={20} className={`text-[var(--ink-soft)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-5 pt-1 space-y-4 animate-fade-up" data-testid={`sc-guide-detail-${p.n}`}>
          <p className="text-sm text-[var(--ink)] leading-relaxed">{p.why}</p>
          {p.tools && <div className="flex flex-wrap gap-2">{p.tools.map((t) => <span key={t} className="text-xs px-3 py-1.5 rounded-full font-medium" style={{ background: c.bg, color: c.fg }}>{t}</span>)}</div>}
          {(p.avoid || p.prefer) && (
            <div className="grid sm:grid-cols-2 gap-3">
              {p.avoid && <div className="rounded-2xl bg-[#FDE4DF] p-4"><div className="flex items-center gap-1.5 text-xs font-bold text-[#B4483A] uppercase tracking-wider mb-1.5"><X size={14} /> À éviter</div><p className="text-sm text-[var(--ink)] italic">{p.avoid}</p></div>}
              {p.prefer && <div className="rounded-2xl bg-[#E4F5EC] p-4"><div className="flex items-center gap-1.5 text-xs font-bold text-[#2F7A55] uppercase tracking-wider mb-1.5"><Check size={14} /> À privilégier</div><ul className="space-y-1">{p.prefer.map((x) => <li key={x} className="text-sm text-[var(--ink)]">{x}</li>)}</ul></div>}
            </div>
          )}
          {p.example && (
            <div className="rounded-2xl border border-[var(--border)] p-4">
              <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: c.fg }}>{p.example.title}</div>
              <ol className="space-y-1.5">{p.example.items.map((it, i) => <li key={i} className="flex items-start gap-2 text-sm text-[var(--ink)]"><span className="w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5" style={{ background: c.ring }}>{i + 1}</span>{it}</li>)}</ol>
            </div>
          )}
          {p.quote && <p className="text-sm italic font-medium rounded-2xl px-4 py-3" style={{ background: c.bg, color: c.fg }}>{p.quote}</p>}
        </div>
      )}
    </div>
  );
};

export default function ScGuide() {
  const [open, setOpen] = useState(1);
  return (
    <div className="space-y-8" data-testid="sc-guide">
      <Panel title={GUIDE_INTRO.title} color="mint" testid="sc-guide-intro">
        <p className="text-sm text-[var(--ink)] leading-relaxed mb-3">{GUIDE_INTRO.text}</p>
        <p className="text-sm italic font-medium rounded-2xl bg-[#E4F5EC] text-[#2F7A55] px-4 py-3">{GUIDE_INTRO.key}</p>
        <div className="mt-4"><GuidePdfButton /></div>
      </Panel>

      <section>
        <h2 className="font-serif-title text-2xl sm:text-3xl text-[var(--gold)] mb-1">Les 10 principes à retenir</h2>
        <p className="text-sm text-[var(--ink-soft)] mb-5">Cliquez sur un principe pour découvrir le pourquoi, les exemples et les phrases à utiliser.</p>
        <div className="grid md:grid-cols-2 gap-4 items-start">
          {PRINCIPLES.map((p) => <PrincipleCard key={p.n} p={p} open={open === p.n} onToggle={() => setOpen(open === p.n ? null : p.n)} />)}
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <Panel title="STOP – RÉFLÉCHIR – AGIR" sub="Une stratégie concrète pour soutenir l'inhibition, à répéter jusqu'à ce qu'elle devienne familière." color="boss" testid="sc-guide-stop">
          <div className="grid grid-cols-3 gap-2">
            {STOP_STRATEGY.map((s, i) => (
              <div key={s.t} className="rounded-2xl p-3 text-center" style={{ background: ["#FDE4DF", "#FFF3D6", "#E4F5EC"][i] }}>
                <div className="text-2xl mb-1">{["✋", "🤔", "🚀"][i]}</div>
                <div className="text-xs font-black tracking-widest text-[var(--ink)]">{s.t}</div>
                <div className="text-[11px] text-[var(--ink-soft)] mt-1 leading-snug">{s.d}</div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Quand l'enfant bloque : la grille « 5 questions »" sub="Chercher une solution fonctionnelle avant de conclure à un problème de comportement." color="obstacle" testid="sc-guide-5q">
          <ol className="space-y-2">{FIVE_QUESTIONS.map((q, i) => <li key={q} className="flex items-center gap-3 text-sm text-[var(--ink)]"><span className="w-7 h-7 rounded-full bg-[#3FA7A3] text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>{q}</li>)}</ol>
        </Panel>
      </div>

      <Panel title="Phrases à remplacer" sub="On parle du comportement, pas de la valeur ou de l'identité de l'enfant." color="arme" testid="sc-guide-phrases">
        <div className="space-y-2">
          {PHRASES.map(([a, b]) => (
            <div key={a} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-sm">
              <div className="rounded-2xl bg-[#FDE4DF] text-[#B4483A] px-3 py-2 line-through decoration-[#B4483A]/50">{a}</div>
              <span className="text-[var(--ink-soft)]">→</span>
              <div className="rounded-2xl bg-[#E4F5EC] text-[#2F7A55] px-3 py-2 font-medium">{b}</div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Par où commencer ? Les priorités" sub="Ne pas multiplier les outils : 1 ou 2 difficultés prioritaires → 1 stratégie → une période d'essai → on évalue, on garde ou on ajuste." color="pouvoir" testid="sc-guide-priorities">
        <div className="flex flex-wrap gap-2">
          {PRIORITIES.map((p, i) => <span key={p} className="inline-flex items-center gap-2 text-sm px-3.5 py-2 rounded-full bg-[#FFF3D6] text-[#B8781A] font-medium"><span className="w-5 h-5 rounded-full bg-[#F2B84B] text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>{p}</span>)}
        </div>
        <p className="mt-4 text-xs text-[var(--ink-soft)] italic">Ces outils répondent déjà à plusieurs difficultés exécutives sans transformer la maison en centre de contrôle aérien.</p>
      </Panel>
    </div>
  );
}
